<template>
    <div>
        <v-row no-gutters class="mb-4">
            <v-col cols="12" class="px-2 pb-2">
                <WarningAlert>
                    <p class="mb-0" style="font-size: 16px;">
                        {{ $t('membership.classes.header_alert') }}
                    </p>
                </WarningAlert>
            </v-col>
        </v-row>

        <v-row v-if="!bCardVisible" class="mx-2" no-gutters>
            <v-col cols="12" md="6">
                <v-select 
                    v-model="participant" 
                    :items="participantOptions" 
                    item-value="userId" 
                    item-text="names"
                    item-disabled="disabled"
                    :return-object="true" 
                    :label="$t('membership.classes.select_the_racer')"
                    :hint="$t('membership.classes.racer_select_hint')" 
                    persistent-hint outlined dense
                    :no-data-text="$t('common.no_data')"
                ></v-select>
            </v-col>
        </v-row>

        <v-row v-if="!bCardVisible && participant" class="mx-2" no-gutters>
            <v-col cols="12" md="6">
                <v-select
                    v-model="membership"
                    :items="memberships"
                    item-value="selectionKey"
                    item-text="name"
                    :return-object="true"
                    :label="$t('membership.classes.organization_name')"
                    persistent-hint
                    :hint="$t('membership.classes.organization_select_hint')"
                    outlined
                    dense
                    :no-data-text="$t('membership.classes.organization_not_found')"
                ></v-select>
            </v-col>
        </v-row>

        <v-row v-if="membership && membership.isSelfManagedClass && !bCardVisible" class="mx-2" no-gutters>
            <v-col cols="12" md="6">
                <v-select 
                    v-model="organizationClassGroup" 
                    :items="organizationClassGroups" 
                    item-value="id"
                    item-text="name" 
                    :return-object="true" 
                    :label="$t('membership.classes.division')"
                    persistent-hint 
                    outlined dense
                    :no-data-text="$t('membership.classes.division_not_found')"
                ></v-select>
            </v-col>
        </v-row>

        <v-row v-if="organizationClassGroup && !bCardVisible" class="mx-2" no-gutters>
            <v-col cols="12" md="6">
                <WarningAlert
                    v-if="divisionWarningVisible"
                    class="mb-2"
                >
                    <p class="mb-0">{{ divisionWarningMessage }}</p>
                </WarningAlert>
                <v-select 
                    v-if="!divisionWarningVisible"
                    v-model="organizationClass" 
                    :items="organizationClassesByGroup" 
                    item-value="id"
                    item-text="name" 
                    item-disabled="disabled"
                    :return-object="true" 
                    :label="$t('membership.classes.class')"
                    persistent-hint 
                    outlined dense
                    :no-data-text="$t('membership.classes.classes_not_found', { userName: participantName, ClassGroupName: organizationClassGroupName })"
                ></v-select>
            </v-col>
        </v-row>

        <v-row v-if="showRaceNumber" class="mx-2" no-gutters>
            <v-col cols="12" md="6">
                <v-autocomplete
                    v-model="raceNumber"
                    :items="displayedRaceNumbers"
                    :search-input.sync="raceNumberSearch"
                    :label="$t('membership.classes.race_number')"
                    persistent-hint
                    outlined dense
                    :no-data-text="$t('membership.classes.race_number_not_found')"
                    :clearable="false"
                    :menu-props="{ closeOnContentClick: true }"
                ></v-autocomplete>
            </v-col>
        </v-row>

        <v-row v-if="membership && !membership.isSelfManagedClass && !bCardVisible" class="mx-2" no-gutters>
            <v-col cols="12" md="6">
                <v-textarea 
                    v-model="emailBody" 
                    :counter="emailBodyLimit" 
                    outlined 
                    name="input-7-4"
                    :label="$t('membership.classes.email_box_lbl')" 
                    value=""
                    class="labelCompleted"
                ></v-textarea>
            </v-col>
        </v-row>

        <v-row v-if="membership && !membership.isSelfManagedClass && !bCardVisible" class="mx-2" no-gutters>
            <v-col cols="12" md="6" class="text-right">
                <v-btn color="primary" dark class="mb-2 center-on-responsive" @click="contactOrganization">
                    {{ $t('membership.classes.send_email') }}
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-show="membership && membership.isSelfManagedClass && !bCardVisible" class="mx-2" no-gutters>
            <v-col cols="12" md="6" class="text-right">
                <v-btn :disabled="bLockSave || (showRaceNumber && !raceNumber)" id="joinClubBtn" color="primary" dark class="mb-2 center-on-responsive"
                    @click="addClassesToExistingMembership">
                    {{ $t('membership.classes.add') }}
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="membership && !bCardVisible && participant" class="mx-2 mt-4" no-gutters>
            <v-col cols="12">
                <v-data-table
                    :headers="participantMembershipHeaders"
                    :items="filteredParticipantMemberships"
                    :loading="loadingParticipantMemberships"
                    dense
                    hide-default-footer
                    disable-pagination
                    :no-data-text="$t('membership.no_data_available')"
                    class="elevation-1 manage-classes-memberships"
                ></v-data-table>
            </v-col>
        </v-row>

        <MembershipCard />
        
        <v-snackbar timeout="2000" v-model="snackbar" :color="color_snackbar" top>
            {{ snackbar_message }}
        </v-snackbar>
    </div>
</template>

<script>
import APIService from "@/services/APIService";
import MembershipService from "@/services/MembershipService";
import MembershipCard from "@/components/membership/MembershipCard";
import WarningAlert from "@/components/alert/WarningAlert";
import moment from "moment";
import { mapState } from "vuex";
import
{
    VRow, VCol, VSelect, VAutocomplete, VTextarea, VBtn, VSnackbar, VDataTable
} from 'vuetify/lib';

export default {
    name: "MembershipRegistration",
    components: {
        MembershipCard,
        WarningAlert,
        VRow, VCol, VSelect, VAutocomplete, VTextarea, VBtn, VSnackbar, VDataTable
    },
    data()
    {
        return {
            snackbar: false,
            color_snackbar: 'success',
            snackbar_message: '',
            participant: null,
            bLoadingParticipants: false,
            bLoadingParticipantsMemberships: false,
            participantsMembershipMap: {},
            membership: null,
            memberships: [],
            pastInfos: [],
            organizationClassGroup: null,
            organizationClassGroups: [],
            organizationClass: null,
            organizationClassesByGroup: [],
            groupedClasses: [],
            groupedClassesKeys: null,
            selectedRClassesAndRNumbers: [],
            currentGroupPastInfos: [],

            skipRaceNumbers: false,
            availableRaceNumbers: [],
            raceNumber: null,
            raceNumberSearch: null,
            // Cap rendered options. Backend can return ~10k numbers when the
            // division uses a wide minNumber..maxNumber range; rendering the
            // full set inside a v-autocomplete blocks the main thread.
            raceNumberRenderLimit: 200,
            participantMemberships: [],
            loadingParticipantMemberships: false,
            currentMembershipPeriod: null,

            activePastInfo: null,
            divisionWarningVisible: false,
            divisionWarningMessage: '',

            emailBody: '',
            emailBodyLimit: 500,
            bLockSave: true,
            noMoreClassesMessage: 'membership.classes.no_more_classes_in_division',
            preselectedUserId: null,
            preselectedOrganizationId: null
        };
    },
    computed: {
        ...mapState({
            userStore: (state) => state.session.user,
            participants: (state) => state.session.participants,
            urlDomain: (state) => state.env.get("VUE_APP_ROOT"),
            bCardVisible: (state) => state.membershipCard.bCardVisible
        }),
        participantLogged() {
            return this.participants.find((participant) => participant.userId == this.userStore.id);
        },
        participantOptions() {
            const users = Array.isArray(this.participants) ? this.participants : [];
            if (!users.length) {
                return [];
            }

            const withMembership = [];
            const withoutMembership = [];

            users.forEach((item) => {
                const hasMembership = this.participantHasEligibleMembership(item.userId);
                const option = { ...item, disabled: !hasMembership };
                if (hasMembership) {
                    withMembership.push(option);
                } else {
                    withoutMembership.push(option);
                }
            });

            const byName = (a, b) => (a.names || "").localeCompare((b.names || ""), undefined, {
                sensitivity: "base",
                numeric: true
            });
            withMembership.sort(byName);
            withoutMembership.sort(byName);

            const items = [];
            if (withMembership.length > 0) {
                items.push({
                    userId: "participants-with-memberships-header",
                    names: this.$t("membership.classes.participants_with_memberships_header"),
                    disabled: true
                });
                items.push(...withMembership);
            }
            if (withoutMembership.length > 0) {
                items.push({
                    userId: "participants-without-memberships-header",
                    names: this.$t("membership.classes.participants_without_memberships_header"),
                    disabled: true
                });
                items.push(...withoutMembership);
            }

            return items;
        },
        participantName() {
            if(this.participant) {
                return this.participant.names
            }
            return '';
        },
        organizationClassGroupName() {
            if(this.organizationClassGroup) {
                return this.organizationClassGroup.name;
            }
            return '';
        },
        selectedClassUsesNumber() {
            return this.classUsesNumber(this.organizationClass);
        },
        showRaceNumber() {
            return this.selectedClassUsesNumber &&
                !this.skipRaceNumbers &&
                !this.bCardVisible;
        },
        participantMembershipHeaders() {
            return [
                { text: this.$t('membership.organization'), value: 'organizationName' },
                { text: this.$t('membership.class'), value: 'className' },
                { text: this.$t('membership.race_num'), value: 'raceNumber' },
                { text: this.$t('membership.membershipNumber'), value: 'membershipNumber' },
                { text: this.$t('membership.membershipPeriod'), value: 'validTo' }
            ];
        },
        filteredParticipantMemberships() {
            const orgId = this.membership && this.membership.organizationId;
            if (!orgId) return [];
            const membershipTypeId = this.membership.membershipTypeId;
            const membershipEventId = this.membership.membershipEventId || null;
            return (this.participantMemberships || []).filter(
                m => m.organizationId == orgId &&
                    m.membershipTypeId == membershipTypeId &&
                    (m.membershipEventId || null) == membershipEventId &&
                    this.isCurrentPeriodMembership(m)
            );
        },
        displayedRaceNumbers() {
            const all = this.availableRaceNumbers || [];
            const limit = this.raceNumberRenderLimit;
            if (all.length <= limit) {
                return all;
            }
            const search = (this.raceNumberSearch || '').toString().trim();
            if (!search) {
                return all.slice(0, limit);
            }
            const matches = [];
            for (let i = 0; i < all.length && matches.length < limit; i++) {
                const v = String(all[i]);
                if (v.indexOf(search) === 0) {
                    matches.push(all[i]);
                }
            }
            // Always keep the currently selected raceNumber visible so the
            // v-autocomplete shows the selection when the user reopens.
            if (this.raceNumber && matches.indexOf(this.raceNumber) === -1) {
                matches.push(this.raceNumber);
            }
            return matches;
        }
    },
    created()
    {
        const { userId, organizationId } = this.$route.query;
        if (userId) this.preselectedUserId = parseInt(userId);
        if (organizationId) this.preselectedOrganizationId = parseInt(organizationId);
        this.getParticipants();
    },
    watch: {
        bCardVisible(status)
        {
            if(!status){
                this.reset();
            }
        },
        participant(racerId)
        {
            this.changeParticipant(racerId);
        },
        participants()
        {
            this.bLoadingParticipants = false;
            this.loadParticipantsMembershipAvailability();
        },
        membership(membership)
        {
            this.organizationClass = null;
            this.organizationClassGroup = null;
            this.organizationClassesByGroup = [];
            this.currentMembershipPeriod = null;
            if (!membership)
            {
                return;
            }
            this.getMemberClasses(membership)
        },
        organizationClassGroup(group)
        {
            this.clearDivisionWarning();
            this.organizationClass = null;
            this.organizationClassesByGroup = [];
            this.activePastInfo = null;
            this.currentGroupPastInfos = [];
            if (!group)
            {
                return;
            }

            const baseGroupClasses = this.groupedClasses[group.id] || [];
            const groupPastInfos = this.pastInfos.filter(item => item.organizationClassesId == group.id);

            // Keep backwards compatibility when stillValid is not present.
            const activeGroupPastInfos = groupPastInfos.filter(item => item.stillValid !== false);
            const registeredClassIds = activeGroupPastInfos.map(item => item.classId);
            this.currentGroupPastInfos = activeGroupPastInfos;

            this.organizationClassesByGroup = group.isMultiReg == 0 ?
                                                    this.groupedClasses[group.id] : 
                                                    this.groupedClasses[group.id].filter(
                                                        cls => !registeredClassIds.includes(cls.id)
                                                    );
            
            // If division is not multi-reg, any existing active class in this group blocks new classes.
            if (Number(group.isMultiReg) === 0 && registeredClassIds.length > 0)
                {
                this.organizationClassesByGroup = [];
                this.organizationClass = null;
                this.raceNumber = null;
                this.bLockSave = true;
                this.setDivisionWarning(this.$t(this.noMoreClassesMessage));
                return;
                }

            // For multi-reg groups, hide already-registered classes from the
            // dropdown entirely. Showing them disabled added noise — the
            // member is here to pick a *new* class, not to look at the ones
            // already on file.
            if (Number(group.isMultiReg) === 1)
            {
                this.organizationClassesByGroup = baseGroupClasses
                    .filter(item => !registeredClassIds.includes(item.id))
                    .map(item => ({ ...item, disabled: false }));
            }
            else
            {
                this.organizationClassesByGroup = baseGroupClasses.map(item => ({ ...item, disabled: false }));
            }
        },
        organizationClass(organizationClass)
        {
            this.clearDivisionWarning();
            this.raceNumber = null;
            this.availableRaceNumbers = [];
            this.skipRaceNumbers = false;
            this.bLockSave = true;
            this.activePastInfo = null;
            if (!organizationClass || organizationClass.isHeader || organizationClass.disabled)
            {
                return;
            }

            if (this.organizationClassGroup && this.organizationClassGroup.isNumberUnique)
            {
                this.activePastInfo = this.currentGroupPastInfos.find(
                    info => info.number != null && info.number !== ''
                ) || null;
            }
            else
            {
                this.activePastInfo = this.currentGroupPastInfos.find(
                    info => info.classId == organizationClass.id
                ) || null;
            }
            // Always ask backend for this class to keep a single source of truth
            // on number usage/availability and avoid type mismatch edge-cases.
            this.getAvailableRaceNumbers();
        },
        emailBody(emailBody, backup)
        {
            if (!emailBody)
            {
                return
            }

            if (emailBody.length > this.emailBodyLimit)
            {
                this.$nextTick(() =>
                {
                    this.emailBody = backup;
                })
            }
        },
        raceNumber(number)
        {
            if (!number)
            {
                return;
            }

            this.bLockSave = false;
        },
        participantLogged(user) {
            if (!user) {
                this.participant = null;
                return;
        }

            // Do not force-select logged user. Keep current only if eligible.
            if (!this.participant || !this.participantHasEligibleMembership(this.participant.userId)) {
                this.selectFirstAvailableParticipant();
            }
        }
    },
    methods: {
        participantHasEligibleMembership(userId) {
            // Strict rule: only users explicitly marked as eligible can be selected.
            if (!Object.prototype.hasOwnProperty.call(this.participantsMembershipMap, userId)) {
                return false;
            }
            return this.participantsMembershipMap[userId] === true;
        },
        isCurrentPeriodMembership(membership) {
            if (!membership) {
                return false;
            }

            const currentPeriod = this.currentMembershipPeriod;
            if (currentPeriod && currentPeriod.startDate && currentPeriod.endDate) {
                return this.membershipDatesOverlapPeriod(
                    membership.validFrom,
                    membership.expirationDate,
                    currentPeriod
                );
            }

            const selectedMembershipTypeId = this.membership && this.membership.membershipTypeId;
            return !selectedMembershipTypeId || membership.membershipTypeId == selectedMembershipTypeId;
        },
        membershipDatesOverlapPeriod(validFrom, validTo, period) {
            const periodStart = moment(period.startDate, "YYYY-MM-DD");
            const periodEnd = moment(period.endDate, "YYYY-MM-DD");
            const membershipTo = moment(validTo, "YYYY-MM-DD");

            if (!periodStart.isValid() || !periodEnd.isValid() || !membershipTo.isValid()) {
                return false;
            }

            if (validFrom) {
                const membershipFrom = moment(validFrom, "YYYY-MM-DD");
                if (membershipFrom.isValid() && membershipFrom.isAfter(periodEnd, "day")) {
                    return false;
                }
            }

            return membershipTo.isSameOrAfter(periodStart, "day");
        },
        getEligibleMemberships(memberships = []) {
            return memberships.filter(membership =>
                new Date(membership.expirationDate) >= new Date() &&
                membership.selfRegistration == 0 &&
                new Date(membership.expirationDate).getFullYear() < 9999 &&
                membership.membershipTypeId != 1
            );
        },
        selectFirstAvailableParticipant(membershipMap = this.participantsMembershipMap) {
            const users = Array.isArray(this.participants) ? this.participants : [];
            if (this.preselectedUserId) {
                const targetUserId = this.preselectedUserId;
                this.preselectedUserId = null;

                const preselected = users.find(item => item.userId == targetUserId);
                if (preselected) {
                    this.participant = preselected;
                    return;
                }
            }

            const loggedUser = this.participantLogged;
            if (loggedUser && membershipMap[loggedUser.userId]) {
                this.participant = loggedUser;
                return;
            }

            const enabled = users.find(item => !!membershipMap[item.userId]);
            this.participant = enabled || null;
        },
        async loadParticipantsMembershipAvailability()
        {
            const users = Array.isArray(this.participants) ? this.participants : [];
            const usersId = users.map(item => item.userId).filter(Boolean);
            if (!usersId.length || this.bLoadingParticipantsMemberships) {
                return;
            }

            this.bLoadingParticipantsMemberships = true;
            try {
                const response = await MembershipService.getMembershipsUsers({
                    usersId,
                    defaulOrganizationId: []
                });

                if (response?.data?.success != 1) {
                    return;
                }

                const eligibleMemberships = this.getEligibleMemberships(response.data.message.memberships || []);
                const groupedByUser = {};
                eligibleMemberships.forEach((item) => {
                    groupedByUser[item.userId] = true;
                });

                const membershipMap = {};
                users.forEach((item) => {
                    membershipMap[item.userId] = !!groupedByUser[item.userId];
                });
                this.participantsMembershipMap = membershipMap;

                const currentParticipantHasMembership = !!(this.participant && membershipMap[this.participant.userId]);
                if (!currentParticipantHasMembership) {
                    this.selectFirstAvailableParticipant(membershipMap);
                }
            } finally {
                this.bLoadingParticipantsMemberships = false;
            }
        },
        classUsesNumber(cls) {
            if (!cls) return false;
            const value = cls.isUsingNumber;
            return !(
                value === null ||
                value === undefined ||
                value === "" ||
                value === 0 ||
                value === "0" ||
                value === false ||
                value === "false"
            );
        },
        setDivisionWarning(message) {
            this.divisionWarningMessage = message;
            this.divisionWarningVisible = true;
        },
        clearDivisionWarning() {
            this.divisionWarningVisible = false;
            this.divisionWarningMessage = '';
        },
        async getParticipants()
        {
            if (this.bLoadingParticipants)
            {
                return;
            }

            if (this.userStore)
            {
                this.bLoadingParticipants = true;
                this.authUserId = this.userStore.id;
                this.$store.dispatch('setParticipantsAction');
                this.loadingProfile = false;
            }
            else
            {
                this.$store.dispatch('setUserAction');
            }
        },
        getMembershipsUsers(data)
        {
            MembershipService.getMembershipsUsers(data).then((response) =>
            {
                if (response.data.success != 1)
                {
                    return;
                }

                const membershipOptions = new Map();
                this.getEligibleMemberships(response.data.message.memberships || [])
                    .forEach(membership =>
                    {
                        const membershipEventId = membership.membershipEventId || null;
                        const selectionKey = [
                            membership.userId,
                            membership.organizationId,
                            membership.organizationMemberTypeid,
                            membershipEventId || 0
                        ].join('-');

                        if (membershipOptions.has(selectionKey)) {
                            return;
                        }

                        const membershipEventName = membership.membershipEventName || "";
                        membershipOptions.set(selectionKey, {
                            selectionKey,
                            organizationId: membership.organizationId,
                            name: membershipEventName
                                ? `${membership.name} — ${membershipEventName}`
                                : membership.name,
                            membershipTypeId: membership.organizationMemberTypeid,
                            membershipEventId: membership.membershipEventId || null,
                            membershipEventName: membership.membershipEventName || "",
                            userId: membership.userId,
                            isSelfManagedClass: membership.isSelfManagedClass
                        });
                    });
                this.memberships = Array.from(membershipOptions.values());

                if (this.preselectedOrganizationId) {
                    const preselectedMembership = this.memberships.find(
                        m => m.organizationId == this.preselectedOrganizationId
                    );
                    if (preselectedMembership) {
                        this.membership = preselectedMembership;
                    }
                    this.preselectedOrganizationId = null;
                }
            });
        },
        async getMemberClasses(membership)
        {
            //this.editedItem.loading = true;
            const data = {
                organizationId: membership.organizationId,
                userId: membership.userId,
                membershipTypeId: membership.membershipTypeId,
                membershipEventId: membership.membershipEventId
            };

            await APIService.fetch({
                method: "post",
                data,
                path: `${this.urlDomain}/laravel/public/api/v1/participant/membership/member-registration/classes`,
            }).then((resp) =>
            {
                if (resp.data.success)
                {
                    const classes = resp.data.message.classes;
                    this.pastInfos = resp.data.message.pastInfos;
                    this.currentMembershipPeriod = resp.data.message.currentPeriod || null;

                    // Group the array by "ocname"
                    this.organizationClassGroups = [];
                    this.groupedClasses = classes.reduce((result, current) =>
                    {
                        const groupId = current.organizationClassesId;
                        const groupName = current.ocname;
                        const isNumberUnique = current.isNumberUnique;
                        //const isMultiReg = current.isMultiReg;

                        if (this.organizationClassGroups.filter(obj => obj.id == groupId).length === 0)
                        {
                            this.organizationClassGroups.push({
                                id: groupId,
                                name: groupName,
                                isNumberUnique,
                                isMultiReg: current.isMultiReg
                            });
                        }

                        if (!result[groupId])
                        {
                            result[groupId] = [];
                        }

                        result[groupId].push(current);

                        return result;
                    }, {});

                    this.groupedClassesKeys = Object.keys(this.groupedClasses);
                    const pastByGroup = (groupId) => this.pastInfos.filter(
                        info => info.organizationClassesId == groupId && info.stillValid !== false
                    );
                    this.organizationClassGroups = this.organizationClassGroups.filter(group =>
                    {
                        const pastRegistrations = pastByGroup(group.id);
                        const hasAnyPast = pastRegistrations.length > 0;

                        if (group.isNumberUnique && hasAnyPast && Number(group.isMultiReg) !== 1)
                        {
                            return false;
                        }

                        if (group.isMultiReg == 0 && hasAnyPast)
                        {
                            return false;
                        }

                        if (group.isMultiReg == 1)
                        {
                            const totalClasses = this.groupedClasses[group.id]?.length || 0;
                            const registeredClasses = pastRegistrations.length;

                            if (registeredClasses >= totalClasses)
                            {
                            return false;
                            }
                        }

                        return true;
                    });
                }
            });
        },
        async getAvailableRaceNumbers()
        {
            const data = {
                userId: this.participant.userId,
                membershipTypeId: this.membership.membershipTypeId,
                organizationId: this.membership.organizationId,
                classId: this.organizationClass.id,
                selectedRClassesAndRNumbers: this.selectedRClassesAndRNumbers,
                membershipEventId: this.membership.membershipEventId,
            };

            await APIService.fetch({
                method: "post",
                data,
                path: `${this.urlDomain}/laravel/public/api/v1/participant/membership/member-registration/available-race-numbers`,
            }).then((resp) =>
            {
                if (resp.data.success)
                {
                    this.skipRaceNumbers = !!resp.data.message.skipRaceNumbers;
                    this.raceNumber = null;

                    if (this.skipRaceNumbers)
                    {
                        this.availableRaceNumbers = [];
                        this.bLockSave = false;
                        return;
                    }

                    this.bLockSave = true;

                    //exclude other participants selected numbers from the number available
                    this.availableRaceNumbers = resp.data.message.availableRaceNumbers;

                    if (this.organizationClassGroup && this.organizationClassGroup.isNumberUnique) {
                        const existingNumber = (this.currentGroupPastInfos || [])
                            .map(info => info.number)
                            .find(n => n != null && n !== '');
                        if (existingNumber != null) {
                            // Lock the dropdown to the number the member
                            // already holds in this division. Don't gate on
                            // availableRaceNumbers.includes(existingNumber):
                            // the backend treats the held number as "in use"
                            // and excludes it from availableRaceNumbers, so
                            // requiring it in that list silently dropped the
                            // lock and let members pick a different number
                            // (which the backend then rejects with
                            // 'number_must_match_group').
                            this.availableRaceNumbers = [existingNumber];
                            this.raceNumber = existingNumber;
                        }
                    } else if (this.activePastInfo) {
                        const match = this.availableRaceNumbers.includes(this.activePastInfo.number);
                        if (match) {
                            this.raceNumber = this.activePastInfo.number;
                        }
                    }
                }
            });
        },
        getAge(birthday)
        {
            const birthdayDay = new Date(birthday);
            const nowDay = new Date();
            let age = nowDay.getFullYear() - birthdayDay.getFullYear();
            let differenceMonths = nowDay.getMonth() - birthdayDay.getMonth();
            if (differenceMonths < 0 || (differenceMonths == 0 && nowDay.getDate() < birthdayDay.getDate()))
            {
                age--;
            }
            return age;
        },
        addClassesToExistingMembership()
        {   
            if (!this.organizationClass || this.organizationClass.disabled || this.organizationClass.isHeader)
            {
                this.snackbar = true;
                this.color_snackbar = 'warning';
                this.snackbar_message = this.$t(this.noMoreClassesMessage);
                return;
            }

            if (this.showRaceNumber && !this.raceNumber)
            {
                this.snackbar = true;
                this.color_snackbar = 'warning';
                this.snackbar_message = this.$t('membership.classes.race_number_not_found');
                return;
            }

            const data = {
                membershipTypeId: this.membership.membershipTypeId,
                participantId: this.participant.userId,
                raceClassId: this.organizationClass.id,
                raceNumber: this.raceNumber,
                eventId: this.membership.membershipEventId
            };
            APIService.fetch({
                method: "post",
                data,
                path: `${this.urlDomain}/laravel/public/api/v1/participant/register/organization/addClassesToExistingMembership`,
            }).then((resp) =>
            {
                if (resp.status == 200 && resp.data.success)
                {
                    this.emailBody = '';

                    // Don't pop the membership-card dialog or wipe the form.
                    // The new class shows up in the table below; the user
                    // typically wants to add another class for the same
                    // racer + organization, so only reset the inputs that
                    // depend on the class choice.
                    this.snackbar = true;
                    this.color_snackbar = 'success';
                    this.snackbar_message = this.$t('membership.classes.add_success');
                    this.organizationClassGroup = null;
                    this.organizationClass = null;
                    this.organizationClassesByGroup = [];
                    this.raceNumber = null;
                    this.activePastInfo = null;

                    // Re-fetch the org's class list + pastInfos so the just-
                    // added class is excluded from the next Division/Class
                    // dropdown picks, and refresh the existing-memberships
                    // table so the new row appears immediately.
                    if (this.membership) {
                        this.getMemberClasses(this.membership);
                    }
                    this.loadParticipantsMembershipAvailability();
                    if (this.participant && this.participant.userId) {
                        this.loadParticipantMemberships(this.participant.userId);
                    }
                }
                else
                {
                    this.snackbar = true;
                    this.color_snackbar = 'error';

                    if (resp?.data?.message === 'user_already_registered')
                    {
                        this.color_snackbar = 'warning';
                        this.snackbar_message = this.$t(this.noMoreClassesMessage);
                        return;
                    }

                    this.snackbar_message = this.$t(`membership.classes.${resp.data.message}`);
                }
            });
        },
        contactOrganization()
        {   
            const data = {
                message: this.emailBody,
                organizationId: this.membership.organizationId,
                participantId: this.participant.userId
            };
            APIService.fetch({
                method: "post",
                data,
                path: `${this.urlDomain}/laravel/public/api/v1/participant/register/organization/addClassesToExistingMembershipRequest`,
            }).then((resp) =>
            {
                if (resp.data.success)
                {
                    this.snackbar = true;
                    this.color_snackbar = 'success';
                    this.snackbar_message = this.$t('membership.classes.email_sended');
                    this.loadParticipantsMembershipAvailability();
                    this.membership = null;
                    this.emailBody = '';
                }
                else
                {
                    this.snackbar = true;
                    this.color_snackbar = 'error';
                    this.snackbar_message = this.$t('membership.classes.email_not_sended');
                }
            });
        },
        reset(){
            this.selectFirstAvailableParticipant();
            this.membership = null;
            this.currentMembershipPeriod = null;
            this.clearDivisionWarning();
        },
        changeParticipant(racerId) {
            this.membership = null;
            this.organizationClassGroup = null;
            this.organizationClassGroups = [];
            this.clearDivisionWarning();
            this.participantMemberships = [];
            this.currentMembershipPeriod = null;
            if (!racerId)
            {
                return;
            }

            const data = {
                "usersId": [this.participant.userId],
                "defaulOrganizationId": []
            }
            this.getMembershipsUsers(data);
            this.loadParticipantMemberships(this.participant.userId);
        },
        async loadParticipantMemberships(userId) {
            if (!userId) {
                this.participantMemberships = [];
                return;
            }
            this.loadingParticipantMemberships = true;
            try {
                const resp = await MembershipService.getAll(userId);
                if (!resp || !resp.data || resp.data.success != 1) {
                    this.participantMemberships = [];
                    return;
                }
                const lifeLabel = this.$t('membership.life_member');
                const list = (resp.data.message || []).map((value) => {
                    const isLife = value.expirationDate === '9999-12-31' || value.periodType === 5;
                    const validTo = isLife
                        ? lifeLabel
                        : moment(new Date(value.expirationDate)).add(1, 'days').format('DD MMM YYYY');
                    const orgClass = value.orgClassName || '';
                    const cls = value.className || '';
                    return {
                        organizationId: value.organizationId,
                        membershipTypeId: value.organizationMemberTypeid,
                        membershipEventId: value.membershipEventId || null,
                        validFrom: value.validFrom || '',
                        expirationDate: value.expirationDate || '',
                        organizationName: value.promoterName || '',
                        className: orgClass && cls ? `${orgClass} - ${cls}` : (orgClass || cls),
                        raceNumber: value.number || '',
                        membershipNumber: value.memberId || '',
                        validTo: validTo
                    };
                });
                // Only keep rows for the selected participant in case the
                // service ever returns mixed rows.
                this.participantMemberships = list;
            } catch (e) {
                this.participantMemberships = [];
            } finally {
                this.loadingParticipantMemberships = false;
            }
        }
    },
};
</script>

<style>
.labelCompleted label.v-label:not(.v-label--active) {
    white-space: normal;
    height: auto;
}

/*
 * Match the header color used elsewhere in the partdev membership UI
 * (Membership.vue defines the same rule globally). Membership.vue's
 * style chunk only loads once that page is mounted, so on a cold visit
 * to /membership/classes the table previously rendered with a white
 * header until the user navigated through /membership and came back.
 * Scope the rule to this table so it applies on first paint.
 */
.manage-classes-memberships.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    background: #cccccc !important;
}
</style>
